import React, { useCallback, useEffect, useState } from "react";
import BrandFooter from "../components/BrandFooter.jsx";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import { api } from "../api/client.js";
import { useLoading } from "../loading/LoadingContext.jsx";
import AppHeader from "../components/AppHeader.jsx";
import LoadingBar from "../components/LoadingBar.jsx";
import { DISPLAY_TIMEZONE_LABEL, amount2, formatClock, money, periodLabelLong } from "./spend/derive.js";

function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

// What a recommendation asks you to do. Factual labels — these come straight
// from the action type AWS reports, not from a judgement about difficulty.
const KIND_LABEL = {
  commitment: "Commit",
  rightsize: "Resize",
  idle: "Idle",
  trend: "Trend",
};

// A source that returned nothing is not the same as one that could not be
// reached, and the difference is actionable: "not enrolled" is a one-off setup
// step, "denied" means the cross-account role has not been redeployed yet.
const STATUS_LABEL = {
  ok: "OK",
  empty: "Nothing found",
  not_enrolled: "Not enrolled",
  // Distinct from "No permission" on purpose: Cost Explorer reports a switched-
  // off feature as an access error, and the fix is a checkbox in the payer's
  // preferences, not an IAM change. Conflating the two sends people to redeploy
  // a role that was never the problem.
  not_enabled: "Not switched on",
  denied: "No permission",
  unavailable: "Unavailable",
};

// Shown UNDER the real AWS message, never instead of it. The first version of
// this page replaced the error with this guess, which hid the one piece of
// evidence that says what actually went wrong — and the guess is not always
// right: a redeployed role can still be denied because Cost Explorer's
// linked-account access is off, or because the call has to come from the
// management account.
const DENIED_HINT =
  "Most often this means the cross-account BillingDashboardReadRole predates the recommendation APIs being added " +
  "to it — re-run cross-account-role/template.yaml in the billing accounts. If it has already been redeployed, " +
  "the message above is AWS's own and names the real cause.";

/**
 * Cost-optimization recommendations from four AWS sources plus one heuristic
 * of our own.
 *
 * The page keeps AWS's estimates and our heuristic visibly apart. AWS says
 * what you would stop paying; the trend rows say what went up and stayed up,
 * which is worth a look but is not a saving anyone has offered. Totalling them
 * together would put a number on the page that nothing stands behind, so the
 * headline counts AWS only and "at risk" is its own figure.
 */
export default function Savings() {
  const { idToken } = useAuth();
  const { start, stop } = useLoading();
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const period = currentPeriod();

  const load = useCallback(
    (force) => {
      setErrorMsg(null);
      if (force) setRefreshing(true);
      start();
      api
        .getSavings(idToken, period, force)
        .then(setData)
        .catch((err) => setErrorMsg(err.message))
        .finally(() => {
          setRefreshing(false);
          stop();
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idToken, period]
  );

  useEffect(() => load(false), [load]);

  const recommendations = data?.recommendations || [];
  const awsItems = recommendations.filter((r) => r.source !== "trend");
  const trendItems = recommendations.filter((r) => r.source === "trend");
  // Bars are scaled within each list, not across both — a $400 trend row next
  // to a $2,100 Savings Plan would otherwise render as a sliver and read as
  // unimportant when it is a different measure entirely.
  const maxAws = awsItems.reduce((m, r) => Math.max(m, r.monthly_savings), 0) || 1;
  const maxTrend = trendItems.reduce((m, r) => Math.max(m, r.monthly_savings), 0) || 1;

  const blockedSources = (data?.sources || []).filter((s) =>
    ["denied", "not_enrolled", "not_enabled", "unavailable"].includes(s.status)
  );

  return (
    <>
      <LoadingBar />
      <AppHeader>
        <div className="freshness">
          <div>
            {data?.cached_at
              ? `Recommendations cached ${formatClock(data.cached_at)} ${DISPLAY_TIMEZONE_LABEL}`
              : "Not loaded"}
          </div>
          <div>
            {data?.degraded
              ? "Some sources failed — this answer is only held for 5 minutes"
              : "AWS regenerates these once a day"}
          </div>
        </div>
        <button type="button" className="primary" onClick={() => load(true)} disabled={refreshing}>
          {refreshing ? "Refreshing…" : data?.degraded ? "Retry now" : "Refresh from AWS"}
        </button>
      </AppHeader>

      <div className="control-rail">
        <div className="rail-group">
          <span className="rail-label">Scope</span>
          <span className="rail-scope">
            All registered accounts · trends compare complete months up to {periodLabelLong(period)}
          </span>
        </div>
        <Link className="rail-scope savings-link" to="/spend">
          Back to Spend →
        </Link>
      </div>

      <div className="spend-page">
        {errorMsg && <div className="error-banner">{errorMsg}</div>}

        <div className="stat-row">
          <div className="stat-cell">
            <div className="stat-kicker">Identified savings</div>
            <div className="stat-big stat-big-good">{money(data?.total_monthly_savings || 0)}</div>
            <div className="stat-note">
              per month, across {data?.recommendation_count || 0}{" "}
              {data?.recommendation_count === 1 ? "recommendation" : "recommendations"}
            </div>
          </div>
          <div className="stat-cell">
            <div className="stat-kicker">Annualised</div>
            <div className="stat-big">{money((data?.total_monthly_savings || 0) * 12)}</div>
            <div className="stat-note">if every recommendation were acted on</div>
          </div>
          <div className="stat-cell">
            <div className="stat-kicker">Spend at risk</div>
            <div className="stat-big">{money(data?.at_risk_monthly || 0)}</div>
            <div className="stat-note">
              per month that rose and stayed up — worth checking, not a saving AWS has offered
            </div>
          </div>
        </div>

        {blockedSources.length > 0 && (
          <div className="source-strip">
            {blockedSources.map((s) => (
              <div className="source-row" key={s.id}>
                <span className={`badge badge-${s.status === "denied" ? "member" : "standalone"}`}>
                  {STATUS_LABEL[s.status] || s.status}
                </span>
                <span className="source-name">{s.label}</span>
                <span className="source-message">
                  {s.message || "No detail returned."}
                  {s.status === "denied" && <span className="source-hint">{DENIED_HINT}</span>}
                </span>
              </div>
            ))}
          </div>
        )}

        <section className="spend-section">
          <div className="section-head section-head-tight">
            <h2>Recommendations</h2>
            <span className="section-meta">Ranked by estimated monthly saving</span>
          </div>
          <p className="section-sub">
            Estimates come from AWS. Acting on a commitment recommendation reduces what rightsizing can then save, so
            the total is a ceiling rather than a sum of independent wins.
          </p>

          <div className="mover-list">
            {awsItems.length === 0 && (
              <div className="pane-empty">
                {data
                  ? "No recommendations from AWS right now. That can mean a genuinely tight estate, or that a source above needs enabling."
                  : "Loading…"}
              </div>
            )}
            {awsItems.map((r, i) => (
              <div className="rec-row" key={`${r.source}-${r.resource || r.title}-${i}`}>
                <span>
                  <span className="mover-service">{r.title}</span>
                  <span className="mover-account">
                    <span className={`kind-tag kind-${r.kind}`}>{KIND_LABEL[r.kind] || r.kind}</span>
                    {[r.account_name, r.resource, r.region].filter(Boolean).join(" · ") || "Organisation-wide"}
                  </span>
                </span>
                <span className="mover-track" aria-hidden="true">
                  <span className="mover-fill mover-fill-save" style={{ width: `${(r.monthly_savings / maxAws) * 100}%` }} />
                </span>
                <span className="mover-delta mover-delta-save">${amount2(r.monthly_savings)}</span>
                <span className="mover-amount">{r.current_monthly_cost ? `of $${amount2(r.current_monthly_cost)}` : "—"}</span>
              </div>
            ))}
          </div>
        </section>

        {trendItems.length > 0 && (
          <section className="spend-section">
            <div className="section-head section-head-tight">
              <h2>Worth checking</h2>
              <span className="section-meta">Computed here, not returned by AWS</span>
            </div>
            <p className="section-sub">
              Service and account pairs whose spend stepped up in a completed month and has not come back down. There is
              no utilisation data behind these — they say something changed, not that it was wrong.
            </p>
            <div className="mover-list">
              {trendItems.map((r, i) => (
                <div className="rec-row" key={`trend-${i}`}>
                  <span>
                    <span className="mover-service">{r.title}</span>
                    <span className="mover-account">
                      <span className="kind-tag kind-trend">{KIND_LABEL.trend}</span>
                      {[r.account_name, r.service].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <span className="mover-track" aria-hidden="true">
                    <span className="mover-fill" style={{ width: `${(r.monthly_savings / maxTrend) * 100}%` }} />
                  </span>
                  <span className="mover-delta mover-delta-up">+${amount2(r.monthly_savings)}</span>
                  <span className="mover-amount">{r.current_monthly_cost ? `of $${amount2(r.current_monthly_cost)}` : "—"}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <BrandFooter className="spend-footer" />
      </div>
    </>
  );
}
